import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    IconButton,
    Button,
    Card,
    CardContent,
    Grid,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    List,
    ListItem,
    ListItemText,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import heartsBg from "./hearts_bnk.png";

export default function Premium({ navigateBack }) {

    const navigate = useNavigate();

    const plans = [
        {
            id: "basic",
            name: "Basic",
            description: "Undo swipes and see who liked you",
            price: 1,
            benefits: ["Undo swipes", "View who liked you"],
        },
        {
            id: "gold",
            name: "Gold",
            description:
                "Undo swipes, see who liked you, who super liked you, and who viewed your profile",
            price: 5,
            benefits: [
                "Undo swipes",
                "View who liked you",
                "View who super liked you",
            ],
        },
        {
            id: "platinum",
            name: "Platinum",
            description:
                "All features + Super Connect (message anyone even without match)",
            price: 10,
            benefits: [
                "Undo swipes",
                "View who liked you",
                "View who super liked you",
                "Get unlimited swipes",
                "Super Connect: message anyone",
            ],
        },
    ];

    const discountRules = [
        { monthsRange: "1-4", discount: 0 },
        { monthsRange: "5-8", discount: 10 },
        { monthsRange: "9-12", discount: 20 },
    ];

    const [selectedPlan, setSelectedPlan] = useState(null);
    const [months, setMonths] = useState(1);
    const [discountPercent, setDiscountPercent] = useState(0);

    useEffect(() => {
        // Update discount based on months
        if (months >= 9) setDiscountPercent(20);
        else if (months >= 5) setDiscountPercent(10);
        else setDiscountPercent(0);
    }, [months]);

    const totalPrice = selectedPlan ? selectedPlan.price * months : 0;
    const discountedPrice = (totalPrice * (1 - discountPercent / 100)).toFixed(2);
    const savings = (totalPrice - discountedPrice).toFixed(2);

    return (
        <Box sx={{
            minHeight: "100vh", // full height
            backgroundImage: `
                    linear-gradient(to right, rgba(255,255,255,0.5), rgba(255,255,255,0.2)),
                    url(${heartsBg})
                    `,
            backgroundSize: "cover", // cover whole area
            backgroundPosition: "center", // center the image
            backgroundRepeat: "no-repeat", // don't repeat
            p: 2, // padding
        }}>
            {/* Back button and header */}
            <Box display="flex" alignItems="center" mb={1}>
                <IconButton
                    onClick={() => navigate("/profile")}
                    sx={{
                        bgcolor: "#f0f0f0",
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        "&:hover": { bgcolor: "#e0e0e0" },
                    }}
                >
                    <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Box ml={2}>
                    <Typography variant="h6">Go Collide Premium</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Select a plan, choose months, see the total and discounts, then click Pay
                    </Typography>
                </Box>
            </Box>

            {/* Plan selection */}
            <Grid container spacing={2} justifyContent="center" mb={4}>
                {plans.map((plan) => (
                    <Grid item xs={12} sm={4} key={plan.id}>
                        <Card
                            sx={{
                                border:
                                    selectedPlan?.id === plan.id
                                        ? "2px solid #010832"
                                        : "1px solid #ccc",
                                cursor: "pointer",
                                "&:hover": { border: "2px solid #010832" },
                            }}
                            onClick={() => setSelectedPlan(plan)}
                        >
                            <CardContent>
                                <Typography
                                    variant="h6"
                                    mb={1}
                                    sx={{
                                        fontWeight: "bold",
                                        color:
                                            plan.name === "Basic" ? "#007bff" : // blue
                                                plan.name === "Gold" ? "#FFD700" : // gold
                                                    "#8e44ad" // nice purple for Platinum
                                    }}
                                >
                                    {plan.name}
                                </Typography>

                                <List dense>
                                    {plan.benefits.map((benefit, idx) => (
                                        <ListItem key={idx} sx={{ py: 0.5 }}>
                                            <ListItemText
                                                primary={`• ${benefit}`}
                                                primaryTypographyProps={{ fontSize: 14 }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                                <Typography variant="subtitle1" color="primary" mt={1}>
                                    ${plan.price}/month
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Months selector */}
            <Box textAlign="center" mb={3}>
                <FormControl sx={{ minWidth: 140 }}>
                    <InputLabel id="months-label">Select Months</InputLabel>
                    <Select
                        labelId="months-label"
                        value={months}
                        label="Select Months"
                        onChange={(e) => setMonths(e.target.value)}
                    >
                        {[...Array(12)].map((_, i) => (
                            <MenuItem key={i + 1} value={i + 1}>
                                {i + 1}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Total & discount */}
            <Box textAlign="center" mb={3}>
                <Typography variant="subtitle1">
                    Total:{" "}
                    {discountPercent > 0 && (
                        <span style={{ textDecoration: "line-through", color: "#999" }}>
                            ${totalPrice.toFixed(2)}
                        </span>
                    )}{" "}
                    <span style={{ fontWeight: "bold", color: "#010832" }}>
                        ${discountedPrice}
                    </span>
                </Typography>
                {discountPercent > 0 && (
                    <Typography variant="caption" color="success.main">
                        You saved ${savings} ({discountPercent}% discount)
                    </Typography>
                )}
            </Box>

            {/* Discount rules */}
            <Grid container spacing={2} justifyContent="center" mb={4}>
                {discountRules.map((rule, idx) => (
                    <Grid item xs={12} sm={4} key={idx}>
                        <Box
                            sx={{
                                bgcolor: "#d4edda",
                                color: "#155724",
                                borderRadius: 2,
                                p: 2,
                                textAlign: "center",
                                fontWeight: "bold",
                            }}
                        >
                            {rule.monthsRange} months → {rule.discount}% Discount
                        </Box>
                    </Grid>
                ))}
            </Grid>

            {/* Proceed button */}
            <Box textAlign="center" mt={3}>
                <Box textAlign="center">
                    <Button
                        variant="contained"
                        size="large"
                        disabled={!selectedPlan}
                        sx={{
                            bgcolor: "#7b3fe4",
                            color: "#fff",
                            fontWeight: "bold",
                            width: { xs: "100%", sm: 300 },
                            py: 1.5,
                            mb: 3,
                            "&:hover": {
                                bgcolor: "#6829c5",
                            },
                        }}
                    >
                        {selectedPlan ? `Proceed to Pay - ${selectedPlan.name}` : "Proceed to Pay"}
                    </Button>
                </Box>

            </Box>


        </Box>
    );
}
